import { useState, useEffect } from "react";
import { ArrowUp, ArrowRight, Download, Shield, Droplets, Thermometer, Zap, Heart, Sparkles, Wrench, FileText, HelpCircle, CheckCircle, Footprints, Wind, Moon, Waves } from "lucide-react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import ImageLightbox from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import FAQ from "@/components/FAQ";
import { productFAQs } from "@/data/faqData";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import l1MainImage from "@/assets/l1-product-main.png";
import l1InstallationImage from "@/assets/l1-installation.png";
import l1SideViewImage from "@/assets/l1-side-view.png";
import l1FeaturesImage from "@/assets/l1-features.png";
import l1AngleViewImage from "@/assets/l1-angle-view.png";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function L1() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: l1MainImage, alt: "ZENO L1 smart bidet toilet with auto open close and foot sensor technology", title: "Main Design" },
    { src: l1InstallationImage, alt: "ZENO L1 wall-mounted smart toilet installation tutorial", title: "Installation Guide" },
    { src: l1SideViewImage, alt: "ZENO L1 smart toilet side profile view showing compact design", title: "Side Profile" },
    { src: l1FeaturesImage, alt: "ZENO L1 smart toilet features and control panel", title: "Features" },
    { src: l1AngleViewImage, alt: "ZENO L1 smart toilet angled view showing LED panel", title: "Angle View" }
  ];

  const keyFeatures = [
    "Auto Open & Close lid functionality",
    "Auto Flush with foot sensor trigger",
    "Foot Sensor for touchless operation",
    "Cleaning Foam Dispenser system",
    "Heated Seat with adjustable temperature",
    "ADA Chair Height Design for accessibility",
    "Instant heating water system",
    "IPX4 Waterproof rating",
    "LED Panel with ambient lighting",
    "Night light for safe navigation",
    "Mobile washing with adjustable position",
    "Female washing mode",
    "Warm air drying function",
    "Nozzle self-cleaning",
    "Eco mode for energy savings",
    "Soft close seat and cover",
    "Wider seat for comfort",
    "Rotary knob control",
    "Bowl pre-moisten feature",
    "Leakage protection plug"
  ];

  const technicalSpecs = [
    { label: "Model", value: "L1" },
    { label: "Dimensions", value: "590 × 390 × 370 mm" },
    { label: "Heating Type", value: "Instant heating type" },
    { label: "Drainage Method", value: "Ground drainage" },
    { label: "P-trap Rough-in", value: "180 mm" },
    { label: "Rated Voltage", value: "110V/220V~" },
    { label: "Water Protection", value: "IPX4 Waterproof" },
    { label: "Seat Material", value: "Antimicrobial composite" },
    { label: "Control Type", value: "LED Panel + Rotary Knob" },
    { label: "Lid Operation", value: "Manual or Automatic flip" }
  ];

  const installationRequirements = [
    {
      title: "Electrical Requirements",
      items: [
        "110V or 220V AC power supply",
        "Dedicated GFCI outlet required",
        "Leakage protection plug included",
        "Professional electrical installation recommended"
      ]
    },
    {
      title: "Plumbing Requirements",
      items: [
        "Water supply line (cold water)",
        "Ground drainage connection",
        "P-trap rough-in: 180mm",
        "Shut-off valve accessible for maintenance"
      ]
    },
    {
      title: "Space Requirements",
      items: [
        "Minimum 590mm width clearance",
        "390mm depth from wall center",
        "370mm height clearance",
        "Additional space for seat opening/closing"
      ]
    }
  ];

  const warrantyInfo = [
    {
      component: "Electronic Components",
      period: "3 years",
      coverage: "Manufacturing defects, control panel, sensors"
    },
    {
      component: "Ceramic Body",
      period: "5 years",
      coverage: "Structural integrity, finish durability"
    },
    {
      component: "Heating System",
      period: "2 years",
      coverage: "Instant heating mechanism, temperature control"
    },
    {
      component: "Water System",
      period: "2 years",
      coverage: "Pump, nozzles, bidet functionality"
    }
  ];

  const faqItems = [
    {
      question: "What are the exact dimensions of the ZENO L1?",
      answer: "The ZENO L1 measures 590mm × 390mm × 370mm, making it a compact design perfect for smaller bathrooms while maintaining full functionality."
    },
    {
      question: "Does the L1 support both manual and automatic lid operation?",
      answer: "Yes, the L1 offers optional manual flip or automatic flip configurations to suit your preference."
    },
    {
      question: "What voltage does the ZENO L1 require?",
      answer: "The L1 supports both 110V and 220V AC power supplies, making it suitable for different regions."
    },
    {
      question: "How does the foot sensor work?",
      answer: "The foot sensor enables touchless operation - simply kick near the sensor to open/close the lid or trigger the flush, promoting hygiene and convenience."
    },
    {
      question: "What is the cleaning foam dispenser?",
      answer: "The integrated foam dispenser pre-moistens the bowl before use, making cleaning easier and more hygienic while reducing water usage."
    },
    {
      question: "Is the ZENO L1 ADA compliant?",
      answer: "Yes, the L1 features ADA Chair Height Design for enhanced accessibility and comfort for users of all abilities."
    }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "ZENO L1 Smart Bidet Toilet",
    "description": "ZENO L1 Smart Bidet Toilet with Auto Open & Close, Auto Flush, Foot Sensor, Cleaning Foam Dispenser, Heated Seat and ADA Chair Height Design. Compact 590x390x370mm dimensions with instant heating and IPX4 waterproof rating.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading",
      "logo": "https://www.aureum7trading.com/lovable-uploads/aureum-vii-trading-logo.png"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "url": "https://www.aureum7trading.com"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/l1-product-main.png",
      "https://www.aureum7trading.com/images/products/l1-installation.png",
      "https://www.aureum7trading.com/images/products/l1-side-view.png",
      "https://www.aureum7trading.com/images/products/l1-features.png",
      "https://www.aureum7trading.com/images/products/l1-angle-view.png"
    ],
    "sku": "ZENO-L1",
    "mpn": "L1",
    "gtin13": "8001234567901",
    "productID": "ZENO-L1",
    "category": "Smart Toilets > Luxury Bathroom Fixtures",
    "material": "Premium Ceramic, Antimicrobial Composite",
    "color": "White",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/l1",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 5,
            "maxValue": 14,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Dimensions",
        "value": "590 × 390 × 370 mm"
      },
      {
        "@type": "PropertyValue",
        "name": "Heating Type",
        "value": "Instant heating type"
      },
      {
        "@type": "PropertyValue",
        "name": "Rated Voltage",
        "value": "110V/220V~"
      },
      {
        "@type": "PropertyValue",
        "name": "Water Protection",
        "value": "IPX4 Waterproof"
      },
      {
        "@type": "PropertyValue",
        "name": "Warranty",
        "value": "5 years comprehensive"
      },
      {
        "@type": "PropertyValue",
        "name": "P-trap Rough-in",
        "value": "180mm"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted text-foreground">
      <SEO
        title="ZENO L1 Smart Bidet Toilet | Foot Sensor & Auto Flush"
        description="ZENO L1 smart bidet toilet: foot-sensor auto open/close, heated seat, foam dispenser, ADA chair height and a compact 590×390×370mm footprint. IPX4 rated."
        keywords="smart toilet, bidet toilet, foot sensor toilet, auto flush toilet, ADA toilet, heated toilet seat, touchless toilet, foam dispenser toilet, ZENO L1, accessible smart toilet, compact smart toilet, European smart toilet"
        structuredData={productStructuredData}
        url="/products/l1"
        image="https://www.aureum7trading.com/images/products/l1-product-main.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "ZENO L1", url: "/products/l1" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-observer">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-medium">
                AVT Compact Series
              </Badge>
              <h1 className="font-playfair text-5xl md:text-6xl font-light mb-6 text-primary">
                ZENO
                <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                  Model L1
                </span>
              </h1>
              <p className="font-inter text-xl text-muted-foreground mb-8 leading-relaxed">
                The ZENO L1 smart bidet toilet combines compact elegance with 26 advanced features including <strong>foot sensor activation</strong>, <strong>auto open/close lid</strong>, <strong>cleaning foam dispenser</strong>, and <strong>ADA chair height design</strong>. This <LocalizedLink to="/smart-toilets" className="text-primary hover:underline font-semibold">intelligent sanitary system</LocalizedLink> delivers touchless hygiene excellence with instant heating water, warm air drying, and ambient LED lighting. Perfect for accessibility-focused bathrooms and modern European homes. Compare with our <LocalizedLink to="/products/luna" className="text-primary hover:underline">LUNA M9 wellness model</LocalizedLink> or browse our complete <LocalizedLink to="/blog/luxury-smart-toilet-guide" className="text-primary hover:underline font-semibold">buying guide</LocalizedLink>.
              </p>

              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Foot sensor touchless operation</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Cleaning foam dispenser</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">ADA chair height accessibility</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Compact 590×390mm footprint</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <PDFDownloadButton productName="ZENO L1" />
                <Button variant="outline" asChild>
                  <LocalizedLink to="/smart-toilets">← Back to Collections</LocalizedLink>
                </Button>
              </div>
            </div>
            <div className="fade-in-observer">
              <div className="relative">
                <img 
                  src={l1MainImage} 
                  alt="ZENO L1 smart bidet toilet featuring foot sensor activation, auto open close lid, cleaning foam dispenser, heated seat, and ADA chair height design for accessible modern bathrooms"
                  className="w-full h-auto rounded-2xl shadow-luxury bg-gradient-to-br from-slate-100 to-slate-200"
                  width="600"
                  height="800"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">Product Gallery</h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              Explore the ZENO L1 from every angle - compact design meets advanced functionality.
            </p>
          </div>
          <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">26 Advanced Features</h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              The ZENO L1 packs comprehensive smart toilet technology into a compact, accessible design.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCardSimple
              icon={<Footprints className="w-8 h-8" />}
              title="Foot Sensor Control"
              description="Touchless lid and flush activation via foot kick sensor for ultimate hygiene"
            />
            <FeatureCardSimple
              icon={<Sparkles className="w-8 h-8" />}
              title="Foam Dispenser"
              description="Integrated cleaning foam pre-moistens bowl for easier, more hygienic cleaning"
            />
            <FeatureCardSimple
              icon={<Thermometer className="w-8 h-8" />}
              title="Instant Heating"
              description="On-demand warm water with adjustable temperature for personalized comfort"
            />
            <FeatureCardSimple
              icon={<Shield className="w-8 h-8" />}
              title="ADA Compliant"
              description="Chair height design ensures accessibility and comfort for all users"
            />
            <FeatureCardSimple
              icon={<Wind className="w-8 h-8" />}
              title="Warm Air Drying"
              description="Adjustable temperature air dryer for complete hands-free experience"
            />
            <FeatureCardSimple
              icon={<Moon className="w-8 h-8" />}
              title="Ambient Lighting"
              description="LED panel and night light for safe, stylish nighttime navigation"
            />
            <FeatureCardSimple
              icon={<Waves className="w-8 h-8" />}
              title="Mobile Washing"
              description="Adjustable nozzle position with buttocks and female wash modes"
            />
            <FeatureCardSimple
              icon={<Zap className="w-8 h-8" />}
              title="Eco Mode"
              description="Energy-saving mode reduces power consumption without sacrificing comfort"
            />
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl">
          <Tabs defaultValue="specs" className="fade-in-observer">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="installation">Installation</TabsTrigger>
              <TabsTrigger value="warranty">Warranty</TabsTrigger>
            </TabsList>
            
            <TabsContent value="specs">
              <Card>
                <CardContent className="p-8">
                  <h3 className="font-playfair text-2xl font-medium text-primary mb-6">Technical Specifications</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {technicalSpecs.map((spec, index) => (
                      <div key={index} className="flex justify-between py-3 border-b border-border/50">
                        <span className="font-inter text-muted-foreground">{spec.label}</span>
                        <span className="font-inter font-medium text-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="installation">
              <Card>
                <CardContent className="p-8">
                  <h3 className="font-playfair text-2xl font-medium text-primary mb-6">Installation Requirements</h3>
                  <div className="grid md:grid-cols-3 gap-8">
                    {installationRequirements.map((req, index) => (
                      <div key={index}>
                        <h4 className="font-inter font-semibold text-lg mb-4 flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-primary" />
                          {req.title}
                        </h4>
                        <ul className="space-y-2">
                          {req.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2 text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                              <span className="font-inter text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="warranty">
              <Card>
                <CardContent className="p-8">
                  <h3 className="font-playfair text-2xl font-medium text-primary mb-6">Warranty Coverage</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {warrantyInfo.map((warranty, index) => (
                      <div key={index} className="p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-5 h-5 text-primary" />
                          <span className="font-inter font-semibold">{warranty.component}</span>
                        </div>
                        <p className="font-inter text-lg text-primary font-medium mb-1">{warranty.period}</p>
                        <p className="font-inter text-sm text-muted-foreground">{warranty.coverage}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-4xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              <HelpCircle className="inline-block w-8 h-8 mr-2 mb-1" />
              Frequently Asked Questions
            </h2>
            <p className="font-inter text-muted-foreground">
              Common questions about the ZENO L1 smart bidet toilet
            </p>
          </div>
          <FAQ items={faqItems} />
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <RelatedProducts currentProduct="L1" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-r from-primary/10 to-accent/10">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
            Ready to Upgrade Your Bathroom?
          </h2>
          <p className="font-inter text-muted-foreground mb-8 max-w-2xl mx-auto">
            Contact our team for pricing, bulk orders, or to schedule a consultation about the ZENO L1 smart bidet toilet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <LocalizedLink to="/contact">
                Request a Quote <ArrowRight className="ml-2 w-4 h-4" />
              </LocalizedLink>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <LocalizedLink to="/compare">Compare Models</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all z-50"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
